import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import WebServiceAmazon from "../../../Services/WebServiceAmazon";
import { useSelector } from "react-redux";
import moment from "moment";
import { ListSkeleton, Skeleton, SkeletonRegion } from "../../Common/Skeleton/Skeleton";

interface CampaignModalProps {
  isOpen: boolean;
  selectedCampaigns?: any[];
  onClose: () => void;
  onApply: (selected: any[]) => void;
}

export const CampaignModal: React.FC<CampaignModalProps> = ({
  isOpen,
  onClose,
  onApply,
  selectedCampaigns = [],
}) => {
  const [selected, setSelected] = useState<any[]>(selectedCampaigns);
  const [loader, setLoader] = useState(false);
  const [list, setList] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const profileData =
    useSelector((state: any) => state.commonReducer.data) || null;
  const portfolioData =
    useSelector((state: any) => state.commonReducer.portfolio) || null;
  const date_range = useSelector((state: any) => state.commonReducer.range);

  const filteredList = list?.filter((item) =>
    item?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // One request in flight. The component stays mounted while closed, so a
  // list asked for, closed on, and answered after an account switch would
  // otherwise fill the next opening with the other account's campaigns.
  const listRequest = useRef<AbortController | null>(null);
  useEffect(() => () => listRequest.current?.abort(), []);

  useEffect(() => {
    if (profileData && isOpen) {
      getList(date_range);
    }
  }, [profileData, portfolioData, date_range, isOpen]);

  const getList = (date: any) => {
    if (profileData == null) {
      return false;
    }
    let body = {
      profileId: profileData.value,
      portfolioId: portfolioData?.value ?? "",
      start_date: moment(date_range.startDate).format("YYYY-MM-DD"),
      end_date: moment(date_range.endDate).format("YYYY-MM-DD"),
    };
    let qurey = new URLSearchParams(body).toString();
    setLoader(true);
    listRequest.current?.abort();
    const controller = new AbortController();
    listRequest.current = controller;
    WebServiceAmazon.getAPI({
      action: `amazon/campaigns?` + qurey,
      body: null,
      signal: controller.signal,
    })
      .then((res: any) => {
        if (controller.signal.aborted) return;
        res.data.length > 0 ? setList(res.data) : setList([]);
        setLoader(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setLoader(false);
      });
  };

  const handleChange = (item: any) => {
    if (
      selected?.some((selected: any) => selected.campaignId === item.campaignId)
    ) {
      setSelected(
        selected.filter(
          (selected: any) => selected.campaignId !== item.campaignId
        )
      );
    } else {
      setSelected([...selected, item]);
    }
  };

  const handleApply = () => {
    onApply(selected);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
      <div className="modal-backdrop fade show" onClick={onClose}></div>
      <div
        className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        role="document"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-content shadow">
          <div className="modal-header">
            <h5 className="modal-title">Campaigns</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            {loader ? (
              // The search box is hidden while loading too, so it gets a placeholder.
              <SkeletonRegion>
                <Skeleton height={38} className="mb-3" />
                <ListSkeleton checkbox lines={1} rows={8} />
              </SkeletonRegion>
            ) : (
              <>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search campaigns..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                {filteredList?.map((item, index) => (
                  <div className="form-check" key={index}>
                    <input
                      className="form-check-input cursor-pointer"
                      type="checkbox"
                      id={item.campaignId}
                      checked={selected?.some(
                        (selected: any) =>
                          selected.campaignId === item.campaignId
                      )}
                      onChange={() => handleChange(item)}
                    />
                    <label
                      className="form-check-label cursor-pointer"
                      htmlFor={item.campaignId}
                    >
                      {item.name}
                    </label>
                  </div>
                ))}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button style={{ height: "38px" }} className="btn btn-primary" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
